import './styles.sass';

import React, { useRef } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import ResearchOutput from 'components/Explorer/models/research_output';
import PageTitle from 'components/Explorer/PageTitle';
import Pagination from 'components/Explorer/Pagination';
import * as QueryParams from 'components/Explorer/models/query_params';
import useExplorerData, { Loading } from 'hooks/useExplorerData';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';

import ResearchOutputItem from './ResearchOutputItem';
import ViewSelector from './ViewSelector';
import SortBy from './SortBy';
import Summary from './Summary';

const trackPageChange = (page) => {
  Analytics.trackEvent('research-outputs-paginate', { page });
};

const Outputs = () => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();
  const rootRef = useRef(null);

  const { data, loading } = useExplorerData('research_outputs', params, {
    reloadOn: ['filters', 'page', 'sortOrder']
  });

  const scrollToTop = () => {
    const wrapper = rootRef.current?.closest('.Explorer-ContentPanel');
    if (wrapper) wrapper.scrollTop = 0;
  };

  // Scroll to top on any reload trigger.
  React.useEffect(() => {
    if (loading === Loading.IN_PROGRESS) scrollToTop();
  }, [loading]);

  const onBadgeClick = (output) => {
    Analytics.trackEvent('research-outputs-open-details-page', { outputId: output.id });
    navigateParams(QueryParams.withShowDetails(params, output.id));
  };

  const outputs = data?.outputs ? data.outputs.map((r) => new ResearchOutput(r)) : [];
  const lastPage = data?.lastPage ?? null;

  const isInitialLoad = data === null && (loading === Loading.IN_PROGRESS || loading === Loading.NOT_STARTED);
  const isUpdating = data !== null && loading === Loading.IN_PROGRESS;

  const content = () => {
    if (loading === Loading.ERROR) {
      return <div className="error-message">{I18n.t('Explorer.Outputs.error')}</div>;
    }
    if (loading === Loading.EMPTY_RESPONSE || (outputs.length === 0 && loading === Loading.SUCCESS)) {
      return <div className="empty-message">{I18n.t('Explorer.Outputs.empty')}</div>;
    }
    return (
      <div className="Explorer-Outputs-Container-Wrapper">
        <div className="Explorer-Outputs-Header">
          <ViewSelector />
          <SortBy />
          <Pagination lastPage={lastPage} onChangePage={trackPageChange} />
        </div>
        <div className={params.view === 'grid' ? 'Explorer-Outputs-Grid' : 'Explorer-Outputs-List'}>
          {outputs.map((output) => (
            <ResearchOutputItem key={`output-${output.id}`} output={output} onBadgeClick={onBadgeClick} />
          ))}
        </div>
        <Pagination lastPage={lastPage} onChangePage={trackPageChange} />
        <div className={`Explorer-Outputs-Updating-spinner ${isUpdating ? 'updating' : 'not-updating'}`} />
      </div>
    );
  };

  return (
    <div className="Explorer-Outputs" ref={rootRef}>
      <PageTitle metaTitle={I18n.t('Explorer.TabBar.outputs')} />
      <Summary />
      <ReactTooltip id="GrantInfo" effect="solid" />

      <div key="content" className="Explorer-Outputs-Container">
        {isInitialLoad ? <div className="Explorer-Outputs-Loading-spinner" /> : content()}
      </div>
    </div>
  );
};

export default Outputs;
